import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useLocale } from '@/hooks';
import { getSafeSchoolNameTKey } from '@/shared/category/getSafeSchoolNameTKey';
import { getSubjectLocaleName } from '@/shared/category/getSubjectLocaleName';
import { type CategoryTree } from '@/shared/category/type';
import { getGradeNumber, isAllGrades } from '@/views/build/util';

import { NONE_VALUE } from '../constant';
import { type CategoryNode } from '../types';

/**
 * CategoryTree 노드 변환을 위한 hook
 * useTranslation을 내부에서 사용하여 타입 안정성 확보
 */
export const useCategoryTreeConversion = () => {
  const { t } = useTranslation(['common', 'build']);
  const { locale } = useLocale();

  /**
   * CategoryTree 노드를 TreeMenuItem으로 변환 (재귀 없이)
   */
  const categoryToMenuItem = useMemo(
    () =>
      (category: CategoryTree): CategoryNode => {
        const label = (() => {
          if (category.name === 'COMMON') {
            return t('common:school.info.shared');
          }
          switch (category.type) {
            case 'SCHOOL_TYPE':
              return t(getSafeSchoolNameTKey(category.name));
            case 'GRADE': {
              const gradeNum = getGradeNumber(category.categoryTreeId);
              return isAllGrades(gradeNum)
                ? String(t('common:school.info.allGrades'))
                : t('common:school.info.nGrade', { n: gradeNum });
            }
            case 'SEMESTER':
              return t('common:school.info.nSemester', {
                n: category.name,
              });
            case 'SUBJECT':
            case 'SUB_SUBJECT':
              return getSubjectLocaleName({
                defaultName: category.name,
                localizeNames: category.localizeNames,
                locale,
              });
            case 'COUNTRY':
              break;
          }
          return category.name;
        })() as string;

        const subNodes = (() => {
          const shouldCastEmpty =
            category.children.length === 0 ||
            (category.children.length === 1 &&
              category.children[0].name === 'COMMON');
          switch (category.type) {
            // 과목 노드는 하위 과목 노드에 none value를 추가
            case 'SUBJECT':
              if (shouldCastEmpty) return [];

              return [
                {
                  nodeId: NONE_VALUE,
                  groupId: NONE_VALUE,
                  label: t('build:select.unselect'),
                  type: 'SUB_SUBJECT',
                  subNodes: [],
                  parentId: category.categoryTreeId.toString(),
                  name: category.name,
                },
                ...category.children.map(child => categoryToMenuItem(child)),
              ];

            case 'COUNTRY':
            case 'SCHOOL_TYPE':
            case 'GRADE':
            case 'SUB_SUBJECT':
            case 'SEMESTER':
            default:
              if (shouldCastEmpty) return [];
              return category.children.map(child => categoryToMenuItem(child));
          }
        })();

        return {
          nodeId: category.categoryTreeId.toString(),
          groupId: category.categoryGroupId.toString(),
          label,
          type: category.type,
          subNodes,
          parentId: category.parentId?.toString(),
          name: category.name,
        };
      },
    [t, locale], // FIX: locale 의존성 추가 - 언어 변경 시 label 업데이트
  );

  /**
   * CategoryTree 배열을 CategoryNode 배열로 변환 (캐싱 최적화)
   * 트리 데이터가 변경될 때만 재계산하여 불필요한 재변환 방지
   */
  const convertTree = useCallback(
    (nodes: CategoryTree[]): CategoryNode[] => {
      return nodes.map(node => categoryToMenuItem(node));
    },
    [categoryToMenuItem],
  );

  return {
    categoryToMenuItem,
    convertTree,
  };
};
